import { useRef, useState } from 'react'
import RunCard from './RunCard'
import { importSolV1 } from '../lib/solV1'
import { proposedSummary } from '../lib/proposals'
import { CATEGORY_SETUP, useEditor, type Tool } from '../store/useEditor'
import { isStrand, type Strand } from '../types/document'
import { PALETTE_PRESETS, PATTERN_REPEATS } from '../lib/palettes'
import { PRODUCTS, PRODUCT_ORDER } from '../lib/products'
import { loadPhotoFile } from '../lib/photo'
import { makeDemoPhoto } from '../lib/demoPhoto'
import { REFERENCES, referenceById } from '../lib/references'
import { garlandColors, stylesFor } from '../lib/decorLibrary'
import type { RenderStats } from '../render/scene'
import { Check, Seg, Section, Slider } from './bits'
import QuotePanel from './QuotePanel'
import Sections from './Sections'

const TOOL_HINTS: Record<string, string> = {
  auto: 'Click where the roof edge starts, then click a short way along the same edge. It follows that edge on its own until the roof turns a corner. If it cannot find the line you still get the run between your two clicks.',
  roofline:
    'Click where the run starts, click where it ends, and it is done. Both clicks snap onto the real roof edge. A roof that turns a corner is two runs, so click the next one the same way.',
  select: 'Click a run to select it, then drag its points to adjust.',
  freehand: 'Press and drag over bushes, hedges and irregular shapes.',
  swag: 'Click the two anchor points. The strand hangs between them.',
  wrap: 'Click the bottom of the trunk, then the top.',
  cluster:
    'Click the branch it hangs from, then where the bottom of the cluster lands. It is one strand folded in half, so it draws as two rows joined at the bottom.',
  pan: 'Drag to move the photo. Scroll to zoom.',
  calibrate: 'Click both ends of the thing you are measuring against.',
}

export default function Rail({
  stats,
  onExport,
  exporting,
}: {
  stats: RenderStats
  onExport: () => void
  exporting: boolean
}) {
  const fileRef = useRef<HTMLInputElement | null>(null)
  const logoRef = useRef<HTMLInputElement | null>(null)
  const [feet, setFeet] = useState('16')
  const [refId, setRefId] = useState('garage2')

  const doc = useEditor((s) => s.doc)
  const tool = useEditor((s) => s.tool)
  const category = useEditor((s) => s.category)
  const setCategory = useEditor((s) => s.setCategory)
  const selectedId = useEditor((s) => s.selectedId)
  const defaults = useEditor((s) => s.defaults)
  const snapEnabled = useEditor((s) => s.snapEnabled)
  const autoTrace = useEditor((s) => s.autoTrace)
  const traceMiss = useEditor((s) => s.traceMiss)
  const calibDraft = useEditor((s) => s.calibDraft)
  const draft = useEditor((s) => s.draft)
  const advanced = useEditor((s) => s.advanced)
  const setAdvanced = useEditor((s) => s.setAdvanced)

  const setTool = useEditor((s) => s.setTool)
  const aiAssist = useEditor((s) => s.aiAssist)
  const rooflineMode = useEditor((s) => s.rooflineMode)
  const setRooflineMode = useEditor((s) => s.setRooflineMode)
  const setPhoto = useEditor((s) => s.setPhoto)
  const setScene = useEditor((s) => s.setScene)
  const setBranding = useEditor((s) => s.setBranding)
  const setWastePct = useEditor((s) => s.setWastePct)
  const setTakeoff = useEditor((s) => s.setTakeoff)
  const addProposals = useEditor((s) => s.addProposals)
  const [solNote, setSolNote] = useState<string | null>(null)
  const solRef = useRef<HTMLInputElement | null>(null)
  const setDefaults = useEditor((s) => s.setDefaults)
  const decorStyle = useEditor((s) => s.decorStyle)
  const setDecorStyle = useEditor((s) => s.setDecorStyle)
  const updateStrand = useEditor((s) => s.updateStrand)
  const updateDecoration = useEditor((s) => s.updateDecoration)
  const selectLayer = useEditor((s) => s.selectLayer)
  const deleteLayer = useEditor((s) => s.deleteLayer)
  const duplicateLayer = useEditor((s) => s.duplicateLayer)
  const applyCalibration = useEditor((s) => s.applyCalibration)
  const clearCalibration = useEditor((s) => s.clearCalibration)
  const commitDraft = useEditor((s) => s.commitDraft)
  const cancelDraft = useEditor((s) => s.cancelDraft)

  const selected = doc.layers.find((l) => l.id === selectedId)
  const strand: Strand | null = selected && isStrand(selected) ? selected : null

  /**
   * Editing a selected strand, or setting up the next one. Changes always land
   * on the defaults too, so whatever you just picked carries to the next run.
   * Picking a color and then drawing, or drawing and then picking, both work.
   */
  const live = strand ?? defaults
  const patch = (p: Partial<Strand>) => {
    if (strand) updateStrand(strand.id, p)
    // A selected wreath takes the color too, so the swatches never look like
    // they did nothing.
    else if (selected && selected.type === 'decoration' && p.palette) {
      updateDecoration(selected.id, { palette: p.palette })
    }
    setDefaults(p as never)
  }

  const onPickFile = async (file: File | undefined) => {
    if (!file) return
    try {
      const p = await loadPhotoFile(file)
      setPhoto(p.dataUrl, p.width, p.height)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'That image could not be opened.')
    }
  }

  const metricsFor = (id: string) => stats.perStrand.find((m) => m.id === id)

  const nightOn = doc.scene.night > 0.01

  return (
    <div className="rail">
      {/*
       * Phone and tablet only. On a small screen the rail becomes a sheet over
       * the photo, and this is the part that stays visible when it is collapsed:
       * the tools, night, and export. Everything a tech needs in a driveway
       * without opening anything.
       */}
      <div className="sheet-head">
        <div className="grab" aria-hidden="true" />
        <div className="sheet-cats">
          {(['roofline', 'garland', 'trees', 'clusters', 'wreaths', 'columns', 'other'] as const).map((c) => (
            <button
              key={c}
              className={category === c ? 'sheet-cat on' : 'sheet-cat'}
              onClick={() => setCategory(c)}
              aria-pressed={category === c}
            >
              {CATEGORY_SETUP[c].label}
            </button>
          ))}
        </div>
        {/*
          Make it night / Night on used to sit here, slamming the scene between
          full dark and daylight with nothing in between. The time of day is a
          drag now, on the sun-to-moon track in the top bar, which stays on
          screen at phone width - so the sheet does not need its own copy.
        */}
        <div className="sheet-actions">
          <button className="btn" onClick={onExport} disabled={!doc.photo || exporting}>
            {exporting ? 'Rendering' : 'Export'}
          </button>
        </div>
      </div>

      <Section title="Photo">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => onPickFile(e.target.files?.[0])}
        />
        <div style={{ display: 'grid', gap: 6 }}>
          <button className="btn wide" onClick={() => fileRef.current?.click()}>
            {doc.photo ? 'Replace photo' : 'Upload a house photo'}
          </button>
          {!doc.photo && (
            <button
              className="btn wide ghost"
              onClick={() => {
                const p = makeDemoPhoto()
                setPhoto(p.dataUrl, p.width, p.height)
              }}
            >
              Try it on a sample house
            </button>
          )}
        </div>
        <p className="hint">Shoot straight on in daylight. The night conversion handles the rest.</p>
      </Section>

      <Section title={CATEGORY_SETUP[category].label}>
        <p className="cat-blurb">{CATEGORY_SETUP[category].blurb}</p>

      {/*
        TWO WAYS TO DRAW A ROOF RUN, AND THEY ARE GENUINELY DIFFERENT.

        The first slides what you drew onto the strongest architectural line
        under it and runs it out to that line's ends, which is what you want on
        a long fascia and wrong on a short recessed edge: the recess is never
        the strongest line near itself, so the run steps onto the neighbor and
        overshoots. The second puts two circles exactly where you clicked and
        draws a straight line between them.
      */}
      {category === 'roofline' && (
        <div className="seg wrap" style={{ marginTop: 8 }}>
          <button
            aria-pressed={rooflineMode === 'follow'}
            onClick={() => setRooflineMode('follow')}
          >
            Follow the roof edge
          </button>
          <button
            aria-pressed={rooflineMode === 'short'}
            onClick={() => setRooflineMode('short')}
          >
            Short edge, exactly two clicks
          </button>
        </div>
      )}

        {category === 'roofline' && (
          <Seg
            options={[
              { value: 'auto' as const, label: 'AI Roofline' },
              { value: 'roofline' as const, label: 'By hand' },
            ]}
            value={tool === 'roofline' ? 'roofline' : 'auto'}
            onChange={setTool}
            accent
          />
        )}
        <div style={{ marginTop: 10 }}>
          <Check
            label="Snap to roof edge"
            checked={snapEnabled}
            onChange={useEditor.getState().setSnap}
          />
        </div>

        {traceMiss > 0 && tool === 'auto' && (
          <p className="warn-note">
            No clear edge to follow from there. Roof edges lost in shade or behind a palm are the
            usual cause. Move your second tap further along the same line, or switch to Roofline and
            tap that run by hand.
          </p>
        )}
        <p className="hint">{TOOL_HINTS[tool] ?? TOOL_HINTS.roofline}</p>
      </Section>

      {(category === 'wreaths' || category === 'garland') && (
        <Section title={category === 'wreaths' ? 'Which wreath' : 'Which garland'}>
          {/*
            Real product, photographed, rather than anything drawn. Picking the
            product is picking the color, because that is how it is bought: you
            do not tint a warm white wreath red, you hang a different wreath.
          */}
          <div className="product-grid">
            {(category === 'wreaths' ? stylesFor('wreath') : garlandColors()).map((st) => {
              const kind = category === 'wreaths' ? ('wreath' as const) : ('garland' as const)
              const on = decorStyle[kind] === st.id
              return (
                <button
                  key={st.id}
                  className="product"
                  aria-pressed={on}
                  onClick={() => {
                    setDecorStyle(kind, st.id)
                    if (selected && selected.type === 'decoration' && selected.kind === kind) {
                      updateDecoration(selected.id, { styleId: st.id })
                    }
                  }}
                >
                  <img src={st.url} alt="" loading="lazy" />
                  <span>{st.label}</span>
                </button>
              )
            })}
          </div>
          <p className="hint">
            {category === 'wreaths'
              ? 'Click the photo to place one, then drag the size below.'
              : 'Pick the bulb color, then frame the opening: the run across the top and a leg down each side.'}
          </p>
        </Section>
      )}

      <Section title={strand ? `Lights: ${strand.name}` : 'Lights for the next run'}>
        <div className="field">
          <div className="field-head">
            <label>Product</label>
            <span className="val">
              {PRODUCTS[live.product].spacingInches}in apart
            </span>
          </div>
          <Seg
            options={PRODUCT_ORDER.map((p) => ({ value: p, label: PRODUCTS[p].label }))}
            value={live.product}
            onChange={(v) => patch({ product: v })}
          />
        </div>

        <div className="field">
          <div className="field-head">
            <label>Color</label>
          </div>
          <div className="presets">
            {PALETTE_PRESETS.map((p) => {
              const on =
                p.colors.length === live.palette.length &&
                p.colors.every((c, i) => c.toLowerCase() === live.palette[i]?.toLowerCase())
              return (
                <button
                  key={p.id}
                  className="preset"
                  aria-pressed={on}
                  onClick={() => patch({ palette: [...p.colors], patternRepeat: p.patternRepeat })}
                >
                  <span className="dots">
                    {p.colors.map((c, i) => (
                      <i key={i} style={{ background: c, color: c }} />
                    ))}
                  </span>
                  <span>{p.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        <div className="field">
          <div className="field-head">
            <label>Custom colors</label>
            <span className="val">{live.palette.length} of 5</span>
          </div>
          <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            {live.palette.map((c, i) => (
              <input
                key={i}
                type="color"
                value={c}
                aria-label={`Color ${i + 1}`}
                style={{
                  width: 30,
                  height: 28,
                  padding: 0,
                  border: '1px solid var(--line)',
                  borderRadius: 6,
                  background: 'transparent',
                }}
                onChange={(e) => {
                  const next = [...live.palette]
                  next[i] = e.target.value
                  patch({ palette: next })
                }}
              />
            ))}
            {live.palette.length < 5 && (
              <button
                className="btn ghost"
                style={{ padding: '5px 9px' }}
                onClick={() => patch({ palette: [...live.palette, '#FF3320'] })}
              >
                +
              </button>
            )}
            {live.palette.length > 1 && (
              <button
                className="btn ghost"
                style={{ padding: '5px 9px' }}
                onClick={() => patch({ palette: live.palette.slice(0, -1) })}
              >
                &minus;
              </button>
            )}
          </div>
        </div>

        {advanced && (
        <div className="field">
          <div className="field-head">
            <label>Pattern repeat</label>
            <span className="val">
              {live.patternRepeat}x{live.patternRepeat}
            </span>
          </div>
          <Seg
            options={PATTERN_REPEATS.map((n) => ({ value: n, label: `${n}x${n}` }))}
            value={live.patternRepeat}
            onChange={(v) => patch({ patternRepeat: v })}
          />
        </div>
        )}

        {(category === 'trees' || category === 'columns') && (
          <>
            {advanced && (
            <Slider
              label="Trunk width"
              value={live.wrapWidth}
              min={0.006}
              max={0.09}
              step={0.001}
              onChange={(v) => patch({ wrapWidth: v })}
              format={(v) =>
                doc.calibration.pixelsPerFoot && doc.photo
                  ? `${((v * doc.photo.width) / doc.calibration.pixelsPerFoot * 12).toFixed(0)} in`
                  : `${(v * 100).toFixed(1)}%`
              }
            />
            )}
            {advanced && (
            <Slider
              label="Wrap tightness"
              value={live.wrapTurns}
              min={3}
              max={40}
              step={1}
              onChange={(v) => patch({ wrapTurns: v })}
              format={(v) => `${v.toFixed(0)} turns`}
            />
            )}
            <Check
              label="Light the cap too"
              checked={live.wrapCap}
              onChange={(v) => patch({ wrapCap: v })}
            />
            {live.wrapCap && (
              <div className="field">
                <div className="field-head">
                  <label>Cap color</label>
                  <span className="val">trunk stays {live.palette.length > 1 ? 'mixed' : 'as set'}</span>
                </div>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  {['#FF3320', '#2BE04B', '#FFB55A', '#FFF4E2', '#3C7BFF'].map((c) => (
                    <button
                      key={c}
                      aria-label={`Cap ${c}`}
                      aria-pressed={live.capPalette[0]?.toLowerCase() === c.toLowerCase()}
                      onClick={() => patch({ capPalette: [c] })}
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: 'var(--radius-sm)',
                        background: c,
                        border:
                          live.capPalette[0]?.toLowerCase() === c.toLowerCase()
                            ? '2px solid var(--accent)'
                            : '1px solid var(--border-subtle)',
                      }}
                    />
                  ))}
                </div>
                <Slider
                  label="Frond length"
                  value={live.capLength}
                  min={0.012}
                  max={0.16}
                  step={0.002}
                  onChange={(v) => patch({ capLength: v })}
                  format={(v) =>
                    doc.calibration.pixelsPerFoot && doc.photo
                      ? `${((v * doc.photo.width) / doc.calibration.pixelsPerFoot).toFixed(1)} ft`
                      : `${(v * 100).toFixed(1)}%`
                  }
                />
                {advanced && (
                <Slider
                  label="Frond droop"
                  value={live.capDroop}
                  min={0}
                  max={1.4}
                  onChange={(v) => patch({ capDroop: v })}
                  format={(v) => (v < 0.25 ? 'stiff' : v > 0.85 ? 'heavy arch' : 'natural')}
                />
                )}
                {advanced && (
                <Slider
                  label="Fronds lit"
                  value={live.capFronds}
                  min={3}
                  max={18}
                  step={1}
                  onChange={(v) => patch({ capFronds: v })}
                  format={(v) => `${v.toFixed(0)}`}
                />
                )}
                <p className="hint">
                  Trunk one color, cap another, and the fronds sized to the tree. A Christmas palm
                  is short and stiff, a royal is long and loose. Fronds are also what you price per
                  leaf, so this is the number that feeds that line.
                </p>
              </div>
            )}
          </>
        )}

        {advanced && (
        <details className="more">
          <summary>Fine tuning</summary>
        <Slider
          label="Bulb size"
          value={live.bulbScale}
          min={0.4}
          max={2.6}
          onChange={(v) => patch({ bulbScale: v })}
          format={(v) => `${v.toFixed(2)}x`}
        />
        <Slider
          label="Spacing"
          value={live.spacingScale}
          min={0.35}
          max={3}
          onChange={(v) => patch({ spacingScale: v })}
          format={(v) =>
            doc.calibration.pixelsPerFoot
              ? `${(PRODUCTS[live.product].spacingInches * v).toFixed(1)} in`
              : `${v.toFixed(2)}x`
          }
        />
        <Slider
          label="Brightness"
          value={live.brightness}
          min={0.3}
          max={1.8}
          onChange={(v) => patch({ brightness: v })}
          format={(v) => `${Math.round(v * 100)}%`}
        />
        <Slider
          label="Sag"
          value={live.sag}
          min={0}
          max={1}
          onChange={(v) => patch({ sag: v })}
          format={(v) => (v < 0.02 ? 'taut' : `${Math.round(v * 100)}%`)}
        />
        <Slider
          label="Perspective, near end"
          value={live.depthStart}
          min={0.3}
          max={1.6}
          onChange={(v) => patch({ depthStart: v })}
          format={(v) => `${v.toFixed(2)}x`}
        />
        <Slider
          label="Perspective, far end"
          value={live.depthEnd}
          min={0.3}
          max={1.6}
          onChange={(v) => patch({ depthEnd: v })}
          format={(v) => `${v.toFixed(2)}x`}
        />
        </details>
        )}

        {strand && (
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <button className="btn" style={{ flex: 1 }} onClick={() => duplicateLayer(strand.id)}>
              Duplicate
            </button>
            <button className="btn danger" onClick={() => deleteLayer(strand.id)}>
              Delete
            </button>
          </div>
        )}
      </Section>

      <Section title="Scene">
        {/*
          The dial, and nothing that moves the dial for you.

          There was an Enhance button above this that read the photo and jumped
          the grade to eighty five percent. The four look settings below it are
          still set from the photo when it loads, because nobody wants to think
          about how a sky is separated from a wall - but the time of day is one
          drag and it is yours.
        */}
        <Slider
          label="Day to night"
          value={doc.scene.night}
          min={0}
          max={1}
          onChange={(v) => setScene({ night: v })}
          format={(v) => (v < 0.03 ? 'as shot' : `${Math.round(v * 100)}%`)}
        />
        {advanced && (
        <details className="more" open>
          <summary>Night look</summary>
          <p className="hint" style={{ marginTop: 0 }}>
            The sky and the house are graded separately, so you can push one
            without flattening the other. Dial it in on this photo and the same
            values carry to the next one.
          </p>
          <Slider
            label="House darkness"
            value={doc.scene.nightDarkness}
            min={0}
            max={1}
            onChange={(v) => setScene({ nightDarkness: v })}
            format={(v) => (v < 0.34 ? 'dusk' : v > 0.7 ? 'deep night' : 'evening')}
          />
          <Slider
            label="Sky darkness"
            value={doc.scene.nightSky}
            min={0}
            max={1}
            onChange={(v) => setScene({ nightSky: v })}
            format={(v) => (v < 0.34 ? 'dusk' : v > 0.72 ? 'midnight' : 'nightfall')}
          />
          <Slider
            label="Contrast"
            value={doc.scene.nightContrast}
            min={0}
            max={1}
            onChange={(v) => setScene({ nightContrast: v })}
            format={(v) => (v < 0.3 ? 'soft' : v > 0.7 ? 'punchy' : 'normal')}
          />
          <Slider
            label="Color"
            value={doc.scene.nightColor}
            min={0}
            max={1}
            onChange={(v) => setScene({ nightColor: v })}
            format={(v) => (v < 0.2 ? 'almost none' : v > 0.7 ? 'full' : 'muted')}
          />
        </details>
        )}

        {advanced && (
        <>
        <Slider
          label="Light on the wall"
          value={doc.scene.spill}
          min={0}
          max={1.6}
          onChange={(v) => setScene({ spill: v })}
          format={(v) => `${Math.round(v * 100)}%`}
        />
        <Slider
          label="Glow"
          value={doc.scene.glow}
          min={0.2}
          max={2.2}
          onChange={(v) => setScene({ glow: v })}
          format={(v) => `${v.toFixed(2)}x`}
        />
        {/*
          Lens star flares on the brightest bulbs. Off by default and deliberately
          under Advanced: a star on every lamp is the clearest tell of a faked
          mockup, so this is a choice somebody makes, not a default they inherit.
        */}
        <Slider
          label="Sparkle size"
          value={doc.scene.sparkle ?? 0}
          min={0}
          max={1}
          onChange={(v) => setScene({ sparkle: v })}
          format={(v) => (v < 0.02 ? 'off' : `${Math.round(v * 100)}%`)}
        />
        {(doc.scene.sparkle ?? 0) > 0.02 && (
          <Slider
            label="How many sparkles"
            value={doc.scene.sparkleAmount ?? 0.3}
            min={0}
            max={1}
            onChange={(v) => setScene({ sparkleAmount: v })}
            format={(v) => (v < 0.05 ? 'a few' : v < 0.5 ? 'scattered' : 'plenty')}
          />
        )}
        {(doc.scene.sparkle ?? 0) > 0.02 && (
          <div className="field">
            <label>Sparkle style</label>
            <div className="seg wrap">
              {(
                [
                  ['star', '8-point'],
                  ['cross', 'Star'],
                  ['bloom', 'Bloom'],
                  ['twinkle', 'Twinkle'],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  aria-pressed={(doc.scene.sparkleStyle ?? 'cross') === id}
                  onClick={() => setScene({ sparkleStyle: id })}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
        <Check
          label="16:9 proposal crop"
          checked={doc.scene.crop169}
          onChange={(v) => setScene({ crop169: v })}
        />
        </>
        )}
      </Section>

      <Section title="Measure">
        {doc.calibration.pixelsPerFoot ? (
          <>
            <div className="readout">
              <div>
                <span className="k">Scale set</span>
                <span className="v">{doc.calibration.knownFeet?.toFixed(2)} ft</span>
              </div>
              <div>
                <span className="k">Per foot</span>
                <span className="v">{doc.calibration.pixelsPerFoot.toFixed(1)} px</span>
              </div>
            </div>
            <button className="btn wide" style={{ marginTop: 8 }} onClick={clearCalibration}>
              Reset scale
            </button>
            <p className="hint">
              One reference gives one scale for the whole photo, so anything much closer or much
              further than the reference reads slightly off. Pick something near the roofline.
            </p>
          </>
        ) : (
          <>
            <div className="field">
              <div className="field-head">
                <label>Measure against</label>
              </div>
              <select
                value={refId}
                onChange={(e) => {
                  const r = referenceById(e.target.value)
                  setRefId(r.id)
                  if (r.feet > 0) setFeet(String(r.feet))
                }}
                style={{
                  width: '100%',
                  background: 'var(--bg)',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--r-sm)',
                  padding: '7px 9px',
                  fontSize: 13,
                }}
              >
                {REFERENCES.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                    {r.feet > 0 ? ` (${r.feet === 6.667 ? "6 ft 8 in" : r.feet + ' ft'})` : ''}
                  </option>
                ))}
              </select>
              <p className="hint">{referenceById(refId).hint}</p>
            </div>

            <button
              className={tool === 'calibrate' ? 'btn wide primary' : 'btn wide'}
              onClick={() => setTool('calibrate')}
            >
              {tool === 'calibrate' ? 'Tap both ends of it' : 'Set the scale'}
            </button>

            {calibDraft.a && calibDraft.b && (
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <input
                  type="number"
                  value={feet}
                  min={0.1}
                  step={0.5}
                  onChange={(e) => setFeet(e.target.value)}
                  aria-label="Known length in feet"
                />
                <button
                  className="btn primary"
                  onClick={() => {
                    if (!doc.photo) return
                    applyCalibration(parseFloat(feet), doc.photo.width, doc.photo.height)
                  }}
                >
                  Apply
                </button>
              </div>
            )}
          </>
        )}
      </Section>

      <Section title="Roof runs to review" right={null}>
        <RunCard />

        {/*
          SOL V1, FROM A SAVED RESPONSE. DEVELOPMENT ONLY.

          import.meta.env.DEV is replaced by the literal false in a production
          build, so this whole block - the button, the file input, the handler
          and its label - is dropped by the bundler and cannot appear in the
          shipped app. A contractor will eventually see one control here that
          says "Generate AI suggestions"; nobody outside development should
          ever be shown a response-file importer.

          It reads a response that already exists on disk: no network call, no
          paid call, no key. Whatever it reads becomes PROPOSALS - unconfirmed,
          unpriced, and refused outright if the response is malformed.
        */}
        {import.meta.env.DEV && advanced && (
          <div style={{ marginTop: 10 }}>
            <button className="btn wide" onClick={() => solRef.current?.click()}>
              Read a saved Sol V1 response (dev)
            </button>
            <input
              ref={solRef}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={async (e) => {
                const f = e.target.files?.[0]
                e.target.value = ''
                if (!f) return
                let body: unknown
                try {
                  body = JSON.parse(await f.text())
                } catch {
                  setSolNote('That file is not JSON. Nothing was imported.')
                  return
                }
                const r = importSolV1(body, {
                  makeId: () => Math.random().toString(36).slice(2, 10),
                  createdAt: new Date().toISOString(),
                  provider: 'sol-v1',
                })
                if (!r.ok) {
                  setSolNote(`Refused: ${r.code} at ${r.at}. ${r.message}. Nothing was imported.`)
                  return
                }
                addProposals(r.import.proposals)
                setSolNote(
                  `${r.import.proposals.length} suggested run${r.import.proposals.length === 1 ? '' : 's'}` +
                    `${r.import.welds ? `, ${r.import.welds} welded at a peak` : ''}. ` +
                    'None of them is counted until you confirm it.',
                )
              }}
            />
            {solNote && <p className="hint">{solNote}</p>}
            <p className="hint">
              Reads a response saved from the frozen Sol V1 run. No network call, no key, no cost.
            </p>
          </div>
        )}
      </Section>

      {/*
        PLACES FIRST, THEN THE LINES DRAWN ON THEM.
        The list below is the drawing. This is the house.
      */}
      <Sections stats={stats.perStrand} />

      <Section title="Runs" right={<span className="val">{doc.layers.length}</span>}>
        {doc.layers.length === 0 ? (
          <p className="hint" style={{ margin: 0 }}>
            {proposedSummary(doc) ?? 'Nothing drawn yet.'}
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 2 }}>
            {[...doc.layers].reverse().map((l) => {
              const m = metricsFor(l.id)
              // Both runs and wreaths carry a palette, so show the real color
              // rather than a placeholder that makes a wreath look unset.
              const color = l.palette?.[0] ?? '#FFB55A'
              return (
                <div
                  key={l.id}
                  className="layer"
                  aria-selected={l.id === selectedId}
                  onClick={() => selectLayer(l.id === selectedId ? null : l.id)}
                >
                  <span className="swatch" style={{ background: color, color: color }} />
                  <span className="name">{l.name}</span>
                  {/* The per-run footage sat here. Hidden while this is a
                      visual design tool; the number is still computed. */}
                  <span className="meta" />
                  <button
                    className="icon-btn"
                    aria-label={`Delete ${l.name}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      deleteLayer(l.id)
                    }}
                  >
                    &times;
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </Section>

      {/*
        PRICING AND TAKEOFF ARE NOT SHOWN, FOR NOW.

        LightsOS is a visual design tool while the measuring is still being
        validated in the field, and a confident dollar figure derived from one
        reference length in one plane of a photograph is exactly the thing not
        to put in front of a customer yet.

        None of it is deleted. The quote is the safety boundary - it is why a
        run has to be included before anything is priced - and it stays wired,
        tested and reachable in a development build, so exposing approximate
        figures to contractors later is turning this flag back on rather than
        rebuilding the foundation.
      */}
      {import.meta.env.DEV && <QuotePanel stats={stats} />}

      {import.meta.env.DEV && advanced && (
      <Section title="Takeoff (development only)">
        <div className="readout">
          <div>
            <span className="k">Linear feet</span>
            {stats.totalFeet !== null ? (
              <span className="v">{stats.totalFeet.toFixed(0)}</span>
            ) : (
              <span className="v na">set the scale</span>
            )}
          </div>
          <div>
            <span className="k">Bulbs</span>
            <span className="v">{stats.totalBulbs}</span>
          </div>
          <div>
            <span className="k">Order, +{doc.takeoff.wastePct}%</span>
            {stats.totalFeet !== null ? (
              <span className="v">
                {Math.ceil((stats.totalFeet * (1 + doc.takeoff.wastePct / 100)) / 5) * 5}
              </span>
            ) : (
              <span className="v na">&mdash;</span>
            )}
          </div>
          <div>
            <span className="k">Runs</span>
            <span className="v">{stats.perStrand.length}</span>
          </div>
        </div>
        {advanced && (
        <div style={{ marginTop: 10 }}>
          <Slider
            label="Waste allowance"
            value={doc.takeoff.wastePct}
            min={0}
            max={30}
            step={1}
            onChange={setWastePct}
            format={(v) => `${v.toFixed(0)}%`}
          />
          {/*
            PERSPECTIVE, AS A DECISION.

            A run receding from the camera really does cover more feet than
            its pixels suggest. What the photograph cannot tell us is whether
            THIS run recedes - and this correction used to be applied to every
            run out of a default, worth 10.25% on everything. Measured at 9.5
            ft on one house, on an eave nearly parallel to the camera.
          */}
          <Check
            label="Correct footage for perspective"
            checked={doc.takeoff.depthCorrection === true}
            onChange={(v) => setTakeoff({ depthCorrection: v })}
          />
          <p className="hint">
            Only for runs that clearly recede from the camera. Off, the footage is the plain tape
            reading from your scale.
          </p>
        </div>
        )}
        <p className="hint">
          Footage comes from the same points that drew the lights, so there is nothing to retype.
          It is an ESTIMATE: one reference length gives one scale for one plane of the photograph.
          Type the figure under the quote line to override it. The order figure adds your waste
          allowance and rounds up to the next 5 ft.
        </p>
      </Section>
      )}

      {advanced && (
      <Section title="Your branding">
        <input
          ref={logoRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          hidden
          onChange={async (e) => {
            const f = e.target.files?.[0]
            if (!f) return
            const url = await new Promise<string>((res, rej) => {
              const r = new FileReader()
              r.onload = () => res(String(r.result))
              r.onerror = () => rej(new Error('That logo could not be read.'))
              r.readAsDataURL(f)
            })
            setBranding({ logoDataUrl: url, show: true })
          }}
        />
        <button className="btn wide" onClick={() => logoRef.current?.click()}>
          {doc.branding.logoDataUrl ? 'Replace logo' : 'Upload your logo'}
        </button>

        {doc.branding.logoDataUrl && (
          <>
            <div style={{ marginTop: 10 }}>
              <Check
                label="Show on every export"
                checked={doc.branding.show}
                onChange={(v) => setBranding({ show: v })}
              />
            </div>
            <div className="field" style={{ marginTop: 6 }}>
              <div className="field-head">
                <label>Corner</label>
              </div>
              <Seg
                options={[
                  { value: 'tl' as const, label: 'Top L' },
                  { value: 'tr' as const, label: 'Top R' },
                  { value: 'bl' as const, label: 'Btm L' },
                  { value: 'br' as const, label: 'Btm R' },
                ]}
                value={doc.branding.corner}
                onChange={(v) => setBranding({ corner: v })}
              />
            </div>
            <Slider
              label="Size"
              value={doc.branding.scale}
              min={0.06}
              max={0.4}
              onChange={(v) => setBranding({ scale: v })}
              format={(v) => `${Math.round(v * 100)}% wide`}
            />
            <Slider
              label="Opacity"
              value={doc.branding.opacity}
              min={0.2}
              max={1}
              onChange={(v) => setBranding({ opacity: v })}
              format={(v) => `${Math.round(v * 100)}%`}
            />
            <button
              className="btn wide danger"
              style={{ marginTop: 4 }}
              onClick={() => setBranding({ logoDataUrl: null })}
            >
              Remove logo
            </button>
          </>
        )}
        <p className="hint">
          Burned into the picture, not added at export, so what you see here is exactly what the
          customer gets. Mockups get forwarded around a neighborhood. Yours should say who made it.
        </p>
      </Section>
      )}

      <Section title="Send it">
        <button className="btn wide primary" onClick={onExport} disabled={!doc.photo || exporting}>
          {exporting ? 'Rendering...' : 'Export image'}
        </button>
        <p className="hint">Full resolution, no watermark. Text it to the customer.</p>

        <button
          className="adv-toggle"
          onClick={() => setAdvanced(!advanced)}
          aria-pressed={advanced}
        >
          {advanced ? 'Hide advanced controls' : 'Show advanced controls'}
        </button>
      </Section>
    </div>
  )
}
